
import { Injectable } from '@angular/core';
import { waitMinutes, waitSeconds } from '@reflact/tsutil';
import { waitForEventSchema, waitForMinutesSchema, waitForSecondsSchema } from '@shared/ragai.schemas';
import { BehaviorSubject, Subject } from 'rxjs';
import { z } from 'zod';
import { EventManagerService } from './event-manager/event-manager.service';
import { ScoreService } from './score/score.service';
import { ScormManagerService } from './scorm-manager.service';

/**
 * Stellt Warte-Hilfsfunktionen für Bausteine und Events bereit (`RAGAI.WAITFOR`).
 * @remarks
 * {@link initialIdle} muss der **allererste** RAGAI-Aufruf in jedem Custom-JS-Baustein sein: Solange der Bot
 * noch initialisiert (Skills laden, Avatar verbindet ggf.), können andere RAGAI-Kommandos ignoriert werden oder
 * fehlschlagen. `RAGAI.FLOW.ready` wird intern genau dann ausgelöst, wenn `initialIdle()` das erste Mal auflöst.
 * {@link nextIdle} ist etwas anderes: es wartet auf den nächsten Ruhezustand nach einem laufenden Bot-Run (z. B.
 * um zu warten, bis die aktuelle Antwort fertig geschrieben ist), nicht auf die initiale Bereitschaft.
 * {@link varsInit} muss abgewartet werden, bevor auf `RAGAI.SCORE`-Werte zugegriffen wird, da deren initiale
 * Werte asynchron aus dem KeyVal-Speicher geladen werden.
 */
@Injectable({
  providedIn: 'root'
})
export class WaitForManagerService {

  public wasInitialIdle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public nextIdleSubject: Subject<boolean> = new Subject<boolean>();
  private wasAvatarConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private avatarConnectionRequired: boolean = false;

  public seconds = waitSeconds;
  public minutes = waitMinutes;


  constructor(
    public eventManager: EventManagerService,
    public score: ScoreService,
    public scormManager: ScormManagerService,
    public eventManagerService: EventManagerService
  ) { }


  /**
   * Wartet eine bestimmte Anzahl Sekunden und führt danach eine Funktion aus.
   * @param seconds Anzahl der zu wartenden Sekunden, oder ein Objekt gemäß `waitForSecondsSchema`.
   * @param fn Die danach auszuführende Funktion.
   */
  public secondsAndExec(seconds: number | z.input<typeof waitForSecondsSchema>, fn?: () => unknown): void {
    const parsed = waitForSecondsSchema.safeParse(seconds);
    if (parsed.success && typeof seconds === 'object') {
      fn = parsed.data.fn as (() => unknown);
      seconds = parsed.data.seconds;
    }
    this.seconds(seconds as number).then(() => { fn?.(); }).catch((e: unknown) => { console.log("Error secondsAndExec fn ", e); });

  }
  /**
   * Wartet eine bestimmte Anzahl Minuten und führt danach eine Funktion aus.
   * @param minutes Anzahl der zu wartenden Minuten, oder ein Objekt gemäß `waitForMinutesSchema`.
   * @param fn Die danach auszuführende Funktion.
   */
  public minutesAndExec(minutes: number | z.input<typeof waitForMinutesSchema>, fn?: () => unknown): void {
    const parsed = waitForMinutesSchema.safeParse(minutes);
    if (parsed.success && typeof minutes === 'object') {
      fn = parsed.data.fn as (() => unknown);
      minutes = parsed.data.minutes;
    }
    this.minutes(minutes as number).then(() => { fn?.(); }).catch((e: unknown) => { console.log("Error minutesAndExec fn ", e); });
  }

  /** @internal Meldet, dass der nächste Idle-Zustand erreicht wurde. */
  public _nextIdleReached() {
    this.nextIdleSubject.next(true);
  }
  /** @internal Meldet, dass der initiale Idle-Zustand erreicht wurde, und löst `RAGAI.FLOW.ready` aus. */
  public _initialIdleReached() {
    if (!this.wasInitialIdle.getValue()) {
      this.wasInitialIdle.next(true);
      queueMicrotask(() => this.eventManager._trigger_RAGAI_EVENT("RAGAI.FLOW.ready", null));

    }
  }
  /** @internal Markiert, dass {@link initialIdle} zusätzlich auf die Avatar-Verbindung warten soll. */
  public _requireAvatarForInitialIdle(): void {
    this.avatarConnectionRequired = true;
  }
  /** @internal Meldet, dass der Avatar verbunden ist. */
  public _avatarConnected(): void {
    this.wasAvatarConnected.next(true);
  }

  /**
   * Wartet, bis ein bestimmtes RAGAI-Event ausgelöst wird.
   * @param hook Name des abzuwartenden Events, oder ein Objekt gemäß `waitForEventSchema`.
   * @returns Die Event-Parameter, sobald das Event ausgelöst wurde.
   */
  public event(hook: string | z.input<typeof waitForEventSchema>) {
    const parsed = waitForEventSchema.safeParse(hook);
    if (parsed.success && typeof hook === 'object') {
      hook = parsed.data.hook;
    }
    const hookStr = hook as string;
    const hookId = `RAGAI.WAITFOR.event-${hookStr}-${crypto.randomUUID()}`;
    return new Promise((resolve) => {
      this.eventManager.on(hookStr, (params) => {
        this.eventManager.off(hookId);
        resolve(params);
      }, hookId);
    });
  }


  /**
   * Wartet auf den nächsten Idle-Zustand des Bots.
   * @returns Aufgelöst, sobald der nächste Idle-Zustand erreicht ist.
   */
  public nextIdle() {
    return new Promise((resolve) => {
      this.nextIdleSubject.subscribe((v) => {
        if (v) {
          resolve(null);
        }
      });
    });
  }

  /**
   * Wartet, bis SCORM initialisiert ist (oder sofort, wenn nicht im LMS).
   * @returns Aufgelöst, sobald SCORM bereit ist.
   */
  public scormInited() {
    console.log("scorm inited done");
    const isInLMS = this.scormManager.runsInLms();
    // NICHTIN LMS
    if (!isInLMS) {
      return new Promise((resolve) => {
        console.log("not in LMS - resolve scormInited");
        resolve(null);
      });
    }

    /// IM LMS UND BEREITS DURHCC
    if (!this.scormManager.isFakeScorm()) {
      return new Promise((resolve) => {
        console.log("in LMS - and real scorm ");
        resolve(null);
      });
    }
    // IM LMS UND NOCH NICHT DURCH

    return new Promise((resolve) => {
      console.log("in LMS - wait for scorm inited");
      this.eventManagerService.on("RAGAI.SCORM.INITED", () => {
        console.log("scorm inited event received ");
        resolve(null);
      });
    });
  }











  /**
   * Wartet, bis der Bot seine Initialisierung abgeschlossen hat und im Ruhezustand ist (optional inkl. Avatar-Verbindung).
   * @returns Aufgelöst, sobald der Bot bereit ist.
   */
  public initialIdle() {
    console.log("return initianIdlePromise");
    return new Promise((resolve) => {
      const tryResolve = () => {
        const idleOk = this.wasInitialIdle.getValue();
        const avatarOk = !this.avatarConnectionRequired || this.wasAvatarConnected.getValue();
        if (idleOk && avatarOk) resolve(null);
      };
      this.wasInitialIdle.subscribe(() => tryResolve());
      this.wasAvatarConnected.subscribe(() => tryResolve());
      tryResolve();
    });
  }

  /**
   * Wartet, bis die initialen Punktestände geladen wurden.
   * @returns Aufgelöst, sobald die Punktestände bereit sind.
   */
  public varsInit() {
    return this.score.waitForInitials();
  }
}
